import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { FaChevronLeft, FaChevronRight } from 'react-icons/fa';

const ThumbnailNavigation = ({ slides, onThumbnailClick, currentThumbnailIndex, onThumbnailIndexChange, disableClick = false, isMobile = false }) => {
  const { t } = useTranslation();
  const [isEditingSlideNumber, setIsEditingSlideNumber] = useState(false);
  const [slideInputValue, setSlideInputValue] = useState('');
  const [slideInputError, setSlideInputError] = useState('');

  // PPT 변경 시 상태 초기화 (slides.length 변경 시에만)
  useEffect(() => {
    if (onThumbnailIndexChange) {
      onThumbnailIndexChange(0);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [slides.length]);

  // 썸네일 네비게이션 함수들
  const goToPrevThumbnail = () => {
    if (currentThumbnailIndex > 0 && onThumbnailIndexChange) {
      onThumbnailIndexChange(currentThumbnailIndex - 1);
    }
  };

  const goToNextThumbnail = () => {
    if (currentThumbnailIndex < slides.length - 1 && onThumbnailIndexChange) {
      onThumbnailIndexChange(currentThumbnailIndex + 1);
    }
  };

  // 슬라이드 번호 클릭 시 입력 모드로 전환
  const handleSlideNumberClick = () => {
    setIsEditingSlideNumber(true);
    setSlideInputValue(String(currentThumbnailIndex + 1));
    setSlideInputError('');
  };

  // 슬라이드 번호 입력 처리
  const handleSlideInputChange = (e) => {
    setSlideInputValue(e.target.value);
    setSlideInputError('');
  };

  // 슬라이드 번호 입력 확정 (Enter 또는 blur)
  const handleSlideInputConfirm = () => {
    const inputNum = parseInt(slideInputValue, 10);

    if (isNaN(inputNum) || inputNum < 1 || inputNum > slides.length) {
      setSlideInputError(t('thumbnailNav.invalidSlideNumber'));
      return;
    }

    if (onThumbnailIndexChange) {
      onThumbnailIndexChange(inputNum - 1);
    }
    setIsEditingSlideNumber(false);
    setSlideInputError('');
  };

  // Enter 키 처리
  const handleSlideInputKeyDown = (e) => {
    if (e.key === 'Enter') {
      handleSlideInputConfirm();
    } else if (e.key === 'Escape') {
      setIsEditingSlideNumber(false);
      setSlideInputError('');
    }
  };

  if (!slides || slides.length === 0) {
    return null;
  }

  return (
    <div style={{
      flex: isMobile ? 'none' : '3',
      width: isMobile ? '100%' : 'auto',
      background: '#ffffff',
      borderRadius: '16px',
      padding: isMobile ? '16px' : '24px',
      boxShadow: '0 4px 20px rgba(0, 0, 0, 0.08)',
      border: '1px solid #e5e7eb',
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      height: isMobile ? 'auto' : 'calc(100vh - 200px)',
      minHeight: isMobile ? 'auto' : '450px',
      maxHeight: isMobile ? 'none' : '600px'
    }}>
      {/* 썸네일 헤더 */}
      <div style={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        marginBottom: isMobile ? '14px' : '24px'
      }}>
        <span style={{
          fontSize: '20px',
          fontWeight: '600',
          color: '#4a5568'
        }}>
          {t('typoViewer.slideInfo', { current: '', total: '' }).split(' ')[0]}{' '}
          {isEditingSlideNumber ? (
            <input
              type="number"
              value={slideInputValue}
              onChange={handleSlideInputChange}
              onBlur={handleSlideInputConfirm}
              onKeyDown={handleSlideInputKeyDown}
              autoFocus
              min={1}
              max={slides.length}
              style={{
                width: '80px',
                fontSize: '24px',
                fontWeight: '700',
                color: '#1A62EA',
                textAlign: 'center',
                border: '2px solid #1A62EA',
                borderRadius: '8px',
                outline: 'none',
                padding: '4px 8px',
                background: '#EBF4FF'
              }}
            />
          ) : (
            <span
              onClick={handleSlideNumberClick}
              style={{
                cursor: 'pointer',
                color: '#1A62EA',
                fontSize: '24px',
                fontWeight: '700',
                padding: '4px 12px',
                border: '1px solid #cbd5e0',
                borderRadius: '8px',
                background: '#f7fafc',
                transition: 'all 0.2s ease'
              }}
              title={t('thumbnailNav.clickToEdit')}
              onMouseEnter={(e) => {
                e.target.style.background = '#EBF4FF';
                e.target.style.borderColor = '#1A62EA';
              }}
              onMouseLeave={(e) => {
                e.target.style.background = '#f7fafc';
                e.target.style.borderColor = '#cbd5e0';
              }}
            >
              {currentThumbnailIndex + 1}
            </span>
          )}
          {' / '}{slides.length}
        </span>
        {slideInputError && (
          <span style={{
            fontSize: '12px',
            color: '#ef4444',
            marginTop: '4px'
          }}>
            {slideInputError}
          </span>
        )}
      </div>

      {/* 썸네일 네비게이션 */}
      <div style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        gap: '16px',
        width: '100%'
      }}>
        {/* 이전 버튼 */}
        <button
          onClick={goToPrevThumbnail}
          disabled={currentThumbnailIndex === 0}
          style={{
            background: currentThumbnailIndex === 0 ? '#e5e7eb' : '#1f2937',
            color: 'white',
            border: 'none',
            borderRadius: '50%',
            width: isMobile ? '24px' : '44px',
            height: isMobile ? '24px' : '44px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            cursor: currentThumbnailIndex === 0 ? 'not-allowed' : 'pointer',
            fontSize: isMobile ? '10px' : '16px',
            transition: 'all 0.2s ease',
            opacity: currentThumbnailIndex === 0 ? 0.5 : 1,
            flexShrink: 0
          }}
          onMouseEnter={(e) => {
            if (currentThumbnailIndex > 0) {
              e.currentTarget.style.transform = 'scale(1.05)';
            }
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.transform = 'scale(1)';
          }}
        >
          <FaChevronLeft />
        </button>

        {/* 썸네일 이미지 */}
        <div style={{
          border: '1px solid #e5e7eb',
          padding: isMobile ? '12px' : '28px',
          borderRadius: '12px',
          overflow: 'hidden',
          boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
          backgroundColor: '#fff',
          position: 'relative',
          cursor: disableClick ? 'default' : 'pointer',
          flex: 1,
          maxWidth: isMobile ? '100%' : '500px',
          width: isMobile ? '100%' : 'auto'
        }}>
          {slides[currentThumbnailIndex] && slides[currentThumbnailIndex].thumbnail_url ? (
            <img
              src={slides[currentThumbnailIndex].thumbnail_url}
              alt={t('thumbnailNav.slideAlt', { num: currentThumbnailIndex + 1 })}
              style={{
                width: '100%',
                height: isMobile ? 'auto' : '350px',
                maxHeight: isMobile ? '250px' : '350px',
                objectFit: 'contain',
                backgroundColor: 'transparent',
                transition: 'all 0.2s ease'
              }}
              onClick={() => !disableClick && onThumbnailClick && onThumbnailClick(currentThumbnailIndex)}
              onMouseEnter={(e) => {
                if (!disableClick) {
                  e.target.style.transform = 'scale(1.01)';
                }
              }}
              onMouseLeave={(e) => {
                if (!disableClick) {
                  e.target.style.transform = 'scale(1)';
                }
              }}
            />
          ) : (
            <div style={{
              width: '100%',
              height: isMobile ? 'auto' : '350px',
              minHeight: isMobile ? '150px' : '350px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              background: '#f9fafb',
              color: '#6b7280',
              fontSize: '14px',
              fontWeight: '500'
            }}>
              {t('thumbnailNav.noThumbnail')}
            </div>
          )}
        </div>

        {/* 다음 버튼 */}
        <button
          onClick={goToNextThumbnail}
          disabled={currentThumbnailIndex === slides.length - 1}
          style={{
            background: currentThumbnailIndex === slides.length - 1 ? '#e5e7eb' : '#1f2937',
            color: 'white',
            border: 'none',
            borderRadius: '50%',
            width: isMobile ? '24px' : '44px',
            height: isMobile ? '24px' : '44px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            cursor: currentThumbnailIndex === slides.length - 1 ? 'not-allowed' : 'pointer',
            fontSize: isMobile ? '10px' : '16px',
            transition: 'all 0.2s ease',
            opacity: currentThumbnailIndex === slides.length - 1 ? 0.5 : 1,
            flexShrink: 0
          }}
          onMouseEnter={(e) => {
            if (currentThumbnailIndex < slides.length - 1) {
              e.currentTarget.style.transform = 'scale(1.05)';
            }
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.transform = 'scale(1)';
          }}
        >
          <FaChevronRight />
        </button>
      </div>
    </div>
  );
};

export default ThumbnailNavigation;